import type { CoreCell, Table as TableInterface } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import type { Virtualizer } from "@tanstack/react-virtual";
import type { Row as RowType } from "@tanstack/table-core";
import { Checkbox } from "monday-ui-react-core";
import { DropdownChevronDown, DropdownChevronRight } from "monday-ui-react-core/dist/allIcons";
import React from "react";
import styled from "styled-components";

import type { Item } from "../../types/common";
import type { ColumnMeta } from "./colums";
import { ITEM_DEPTH, SUBITEM_DEPTH } from "./colums";

export interface TableMeta {
  depth?: number;
  color: string;
}

export const Table = styled.div<{ expanded: boolean }>`
  display: grid;
  flex-direction: column;
  position: relative;
  margin-bottom: ${({ expanded }) => (expanded ? 20 : 0)}px;
  transition: all 100ms linear;
  transform: translateY(${({ expanded }) => (!expanded ? -38 : 0)}px);
  border-right: none;
  background-color: var(--secondary-background-color);
`;

export const TableRow = styled.div<{ height?: number; showExpander?: boolean; hoverEffect?: boolean }>` 
  display: inline-flex;
  position: relative; 
  background-color: var(--secondary-background-color);
  &.selected, &.selected > .cell:not(:first-child) {
    background-color: rgb(var(--primary-selected-color-rgb)); 
  }
   
  &:last-child, &:last-child > .cell:first-child  .color{ 
    border-bottom-left-radius: 6px;  
  }
    
  ${({ hoverEffect }) =>
    hoverEffect
      ? `
        &:hover { 
          background-color: var(--grey-background-color);
        }
        &:hover > .cell:not(:first-child) {   
          background-color: var(--grey-background-color);
        } `
      : ""};
    
  &:hover:not(:first-child) > .cell:not(:first-child) {   
    background-color: var(--grey-background-color);
  }
   
  &.headers{
     border-top-left-radius: 4px; 
       height: ${({ height = 38 }) => height}px;
     border-top: solid 1px var(--layout-border-color);
     border-bottom: solid 1px var(--layout-border-color);
  .cell:first-child .color {
    border-top-left-radius: 6px;
    }
  } 

  .open-widget { 
    display: none;
    font-size: 12px;
    align-items: center;
    gap: 4px; 
    padding-right 6px;
  }

  &:hover .open-widget {
    display: flex;
    cursor: pointer;
  }
  
  &.sticky {
    position: sticky;
    z-index: 2;
    top: ${({ showExpander }) => (showExpander ? 40 : 0)}px;
   }
`;

export const Cell = styled.div<{
  width?: number;
  align?: string;
  color?: string;
  height?: number;
  sticky?: boolean;
  left?: number;
}>`
  :not(last-child) {
    border-right: 1px solid var(--layout-border-color);
  }
  height: ${({ height = 36 }) => height}px;
  padding: ${({ color }) => (color ? 0 : "8px 12px")};
  box-sizing: border-box;
  overflow: hidden;
  overflow: ${({ color }) => (color ? "initial" : "hidden")};
  background-color: ${({ color }) => color || "var(--secondary-background-color)"};
  width: ${({ width }) => width}px;
  min-width: ${({ width }) => width}px;
  text-align: ${({ align = "center" }) => align};
  position: ${({ sticky }) => (sticky ? "sticky" : "static")};
  left: ${({ left = 0 }) => left}px;
`;

export const FlexCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Badge = styled.div`
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  height: 20px;
  font-size: 12px;
  padding: 0 4px;
  background-color: var(--primary-background-hover-color);
`;

export const SubItems = styled.div`
  padding: 20px 0px 20px 0px;
  border-right: none;
  display: flex;
  background-color: var(--primary-background-color);
  border-top: 1px solid var(--layout-border-color);

  & > .inner {
    display: flex;
    width: 100%;
    flex-direction: column;
    border-top-left-radius: 8px;
    border-top: 1px solid var(--layout-border-color);
    border-bottom: 1px solid var(--layout-border-color);

    border-bottom-left-radius: 8px;
  }
`;

export const SubItemsToggle = styled.div<{ hasSubItems: boolean }>`
  display: inline-flex;
  border-radius: 4px;
  opacity: ${({ hasSubItems }) => (hasSubItems ? 1 : 0)};
  &:hover {
    background-color: var(--primary-background-hover-color);
  }
`;

export const Heading = styled.div<{ color: string }>`
  display: flex;
  position: sticky;
  top: 0px;
  left: 0px;
  z-index: 3;
  align-items: center;
  height: 40px;
  gap: 5px;
  color: ${({ color }) => color};
  padding-left: 20px;
  & > h4 {
    font-size: 18px;
    font-weight: 500;
    margin: 0;
  }
`;

const Name = styled.span`
  padding: 0px 4px;
  white-space: nowrap;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const NameContainer = styled.div`
  display: flex;
  height: 100%;
  & > .name {
    overflow: hidden;
    padding-right: 12px;
  }
  justify-content: space-between;

  & > * {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .icon {
    color: var(--layout-border-color);
    padding: 0 6px 0 16px;
    position: relative;
    transition: all 0.3s ease;
    &:hover {
      cursor: pointer;
      color: var(--primary-color);
    }
    &:before {
      position: absolute;
      width: 1px;
      height: 36px;
      content: "";
      display: block;
      background: var(--layout-border-color);
      top: -6px;
      left: 0px;
    }
  }
`;

export const Scroller = styled.div`
  display: grid;
  flex-direction: column;
  overflow: auto;
`;

export const StickyTitle = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--primary-background-color);

  &.expanded {
    background: transparent;
  }
`;

export const Indent = styled.div`
  width: 20px;
  background: var(--primary-background-color);
  flex-shrink: 0;
  position: sticky;
  left: 0;
  z-index: 1;
`;

export const GroupsColor = styled.div`
  background: var(--secondary-background-color);
  margin-top: -1px;
  & > .color {
    width: 100%;
    height: 38px;
  }
`;

interface CellProps {
  cell: CoreCell<Item, unknown>;
  isExpanded: boolean;
}

interface ChevronToggleProps {
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  expanded: boolean;
  size?: string;
  hasSubItems?: boolean;
}

interface HeaderProps {
  table: TableInterface<Item>;
  group: {
    color: string;
    title: string;
  };
  expanded?: boolean;
  showExpander?: boolean;
  subRows?: RowType<Item>[];
}

interface RowProps {
  row: RowType<Item>;
  color: string;
  isHighlighted: boolean;
  onClick?: (item: Item) => void;
  isSelected?: boolean;
  canSelect?: boolean;
  isExpanded?: boolean;
  style?: object;
  rowVirtualizer?: Virtualizer<HTMLDivElement, Element>;
  index?: number;
  children?: React.ReactNode;
  isExpandable?: boolean;
}

export function ChevronToggle({ onClick, expanded, hasSubItems = true }: ChevronToggleProps) {
  const icon = expanded ? <DropdownChevronDown /> : <DropdownChevronRight />;
  return (
    <SubItemsToggle onClick={onClick} hasSubItems={hasSubItems}>
      {icon}
    </SubItemsToggle>
  );
}

export const MemoizedItem = React.memo(
  function ItemName({ cell, isExpanded }: CellProps) {
    const hasSubItems = cell.row.subRows.length > 0;
    const onToggleHandler = cell.row.getToggleExpandedHandler();

    function onClick(e: React.MouseEvent<HTMLDivElement>) {
      e.stopPropagation();
      return onToggleHandler();
    }

    return (
      <NameContainer>
        <div className="name">
          <ChevronToggle onClick={onClick} expanded={isExpanded} hasSubItems={hasSubItems} />
          <Name>{cell.getValue() as string}</Name>
          {hasSubItems && <Badge>{cell.row.subRows.length}</Badge>}
        </div>
      </NameContainer>
    );
  },
  function (prev, next) {
    return prev.isExpanded === next.isExpanded;
  }
);

export function Headers({ table, group, showExpander }: HeaderProps) {
  const [{ headers, id }] = table.getHeaderGroups();
  return (
    <TableRow key={id} className="sticky headers" showExpander={showExpander}>
      {headers
        .filter((h) => {
          const columnMeta = h.column.columnDef.meta as ColumnMeta;
          return columnMeta?.depth?.includes(ITEM_DEPTH);
        })
        .map(({ id, column, getContext }) => {
          const columnMeta = column.columnDef.meta as ColumnMeta;
          return (
            <Cell
              key={id}
              className="cell"
              width={column.getSize()}
              sticky={columnMeta?.sticky}
              left={column.getStart()}
              color={column.columnDef.id === "color" ? group.color : undefined}
            >
              {flexRender(column.columnDef.header, getContext())}
            </Cell>
          );
        })}
    </TableRow>
  );
}

export function SubHeaders({ table, group, subRows = [] }: HeaderProps) {
  const [{ headers }] = table.getHeaderGroups();
  const subHeaders = headers.filter((h) => {
    const columnMeta = h.column.columnDef.meta as ColumnMeta;
    return columnMeta?.depth?.includes(SUBITEM_DEPTH);
  });
  const ids = subRows.map((r) => r.id);
  const selectedFlatRows = table.getSelectedRowModel().flatRows;
  const isAllSelected = ids.every((id) => selectedFlatRows.map((r) => r.id).includes(id));
  const isSomeSelected = !isAllSelected && ids.some((id) => selectedFlatRows.map((r) => r.id).includes(id));
  const depth = (table.options.meta as TableMeta)?.depth;
  const isDisabled = Number.isInteger(depth) && depth === ITEM_DEPTH;

  function onToggleAllSubRowsHandler() {
    if (!isAllSelected) {
      const selection = ids.reduce((selected, id) => {
        return { ...selected, [id]: true };
      }, {});
      table.setRowSelection((prev) => ({ ...prev, ...selection }));
    } else {
      table.setRowSelection((prev) => {
        return Object.keys(prev)
          .filter((id) => !ids.includes(id))
          .reduce((obj, key) => ({ ...obj, [key]: true }), {});
      });
    }
  }

  return (
    <TableRow>
      {subHeaders.map(({ id, column, getContext }) => {
        const columnId = column.columnDef.id;
        const columnMeta = column.columnDef.meta as ColumnMeta;
        const color = columnId === "color" ? group.color : undefined;
        const left = getIndent(column.getStart(), SUBITEM_DEPTH, column.id);
        return (
          <Cell
            key={id}
            width={column.getSize()}
            color={color}
            sticky={columnMeta?.sticky}
            left={left}
            className="cell"
          >
            {columnId !== "select" ? (
              flexRender(column.columnDef.header, getContext())
            ) : (
              <Checkbox
                disabled={isDisabled}
                indeterminate={isSomeSelected}
                checked={isAllSelected}
                onChange={onToggleAllSubRowsHandler}
              />
            )}
          </Cell>
        );
      })}
    </TableRow>
  );
}

function getIndent(offset: number, depth: number, id: string) {
  const indent = depth === 0 ? offset : offset + 20;
  const ITEM_COLUMN_WIDTH = 500;
  if (id !== "subitem") {
    return indent;
  } else {
    return indent - ITEM_COLUMN_WIDTH;
  }
}

export const VirtualItem = styled.div`
  &:last-child .row-container,
  &:last-child .row-container > .cell:first-child .color {
    border-bottom-left-radius: 6px;
  }

  .row-container {
    border-top: 1px solid var(--layout-border-color);
  }
  &:last-child .row-container {
    border-bottom: 1px solid var(--layout-border-color);
  }
`;

const RowContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  width: 100%;
`;

export function Row({
  row,
  color,
  isHighlighted,
  onClick,
  rowVirtualizer,
  index,
  children,
  isExpanded,
  isExpandable,
}: RowProps) {
  return (
    <RowContainer ref={(el) => rowVirtualizer?.measureElement(el)} data-index={index}>
      <TableRow className={isHighlighted ? "selected row-container" : "row-container"} hoverEffect={true}>
        {row
          .getVisibleCells()
          .filter((c) => {
            const columnMeta = c.column.columnDef.meta as ColumnMeta;
            return columnMeta?.depth?.includes(row.depth);
          })
          .map(({ id, column, getContext }) => {
            const columnMeta = column.columnDef.meta as ColumnMeta;
            const left = getIndent(column.getStart(), row.depth, column.id);
            return (
              <Cell
                align="left"
                className="cell"
                key={id}
                width={column.getSize()}
                sticky={columnMeta.sticky}
                left={left}
                color={column.columnDef.id === "color" ? color : undefined}
                onClick={() => onClick?.(row.original)}
              >
                {flexRender(column.columnDef.cell, getContext())}
              </Cell>
            );
          })}
      </TableRow>
      <div>{isExpanded && isExpandable && children}</div>
    </RowContainer>
  );
}
